import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import RepExample from "@/components/RepExample";
import PageMeta from "@/components/PageMeta";
import { SITE, has } from "@/config/site";
import { CheckCircle, LinkedinLogo, EnvelopeSimple, Phone, WhatsappLogo, MapPin } from "@phosphor-icons/react";

export default function About() {
  const lending = SITE.LENDING_ENABLED;
  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <PageMeta
        title="About — Ascentremit"
        description="Ascentremit lets parents in Nigeria spread school fees over 3 to 9 affordable monthly payments. The school is paid in full on day one."
      />
      <PublicNav />

      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-16 py-14 lg:py-20">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#132A13] font-medium leading-tight">
          Don't let the term bill decide your child's term.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#4F5D4F] leading-relaxed">
          Ascentremit lets parents in Nigeria spread school fees over {SITE.TENORS_MONTHS[0]} to{" "}
          {SITE.TENORS_MONTHS[SITE.TENORS_MONTHS.length - 1]} affordable monthly payments. The school is paid in full
          on day one, so your child starts the term on time.
        </p>

        {/* Why we started */}
        <div className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">Why we started</h2>
          <p className="mt-4 text-sm sm:text-base text-[#4F5D4F] leading-relaxed">
            Every January, April and September, millions of families face a school bill that lands all at once, while
            salaries arrive monthly. Many parents end up borrowing from expensive informal lenders, or children miss
            the first weeks of term.
          </p>
          {has(SITE.FOUNDER_NAME) && has(SITE.FOUNDER_STORY_SENTENCE) && (
            <p className="mt-4 text-sm sm:text-base text-[#4F5D4F] leading-relaxed" data-testid="about-founder-para">
              {SITE.FOUNDER_NAME.split(" ")[0]} started Ascentremit to fix that mismatch.{" "}
              {SITE.FOUNDER_STORY_SENTENCE}
            </p>
          )}
        </div>

        {/* What we do */}
        <div className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">What we do</h2>
          <div className="grid gap-5 md:grid-cols-3 mt-6">
            {lending && (
              <Card
                tag="For parents"
                title="School fees in monthly instalments."
                body="Apply in minutes, see your exact monthly payment and total cost before you accept, and repay over 3 to 9 months. You can also pay in full."
              />
            )}
            <Card
              tag="For schools"
              title="Full fees, on time, with no risk."
              body="When a parent chooses instalments, we pay the school the whole fee upfront. The school never chases the balance and never carries the debt. Joining is free."
            />
            <Card
              tag="For agents"
              title="Earn by introducing schools and families."
              body="Introduce schools and families to Ascentremit and earn a monthly commission for as long as they use us."
            />
          </div>
        </div>

        {/* How our instalments are different */}
        {lending && (
          <div className="mt-14">
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">
              How our instalments are different
            </h2>
            <ol className="mt-6 space-y-5">
              <HowItem
                n={1}
                h="The price is on the page."
                b="Your interest rate, monthly payment, total repayment and late fee are shown before you sign."
              />
              <HowItem
                n={2}
                h="We lend only what you can repay."
                b="We check affordability before every loan. If the full term looks too much, we'll suggest a smaller amount or a longer plan rather than set you up to fail."
              />
              <HowItem
                n={3}
                h="Respectful repayment."
                b="If you miss a payment, we talk to you — never your family, friends, employer or phone contacts. We never ask for access to your contacts, photos or messages."
              />
              <HowItem
                n={4}
                h="Your child is never involved."
                b="The school is already paid. We never ask a school to send a pupil home or withhold results over a payment owed to us."
              />
              {!SITE.EARLY_REPAYMENT_PENALTY && (
                <HowItem
                  n={5}
                  h="Pay off early, save on interest."
                  b="Settle any time with no penalty."
                />
              )}
            </ol>
          </div>
        )}

        {/* Who stands behind your loan */}
        {lending && (has(SITE.LENDING_ENTITY) || has(SITE.CREDIT_BUREAU)) && (
          <div className="mt-14">
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">
              Who stands behind your loan
            </h2>
            <ul className="mt-5 space-y-3 text-sm">
              {has(SITE.LENDING_ENTITY) && (
                <Bullet>Instalment plans are provided by <span className="font-semibold text-[#132A13]">{SITE.LENDING_ENTITY}</span>.</Bullet>
              )}
              {has(SITE.CREDIT_BUREAU) && (
                <Bullet>
                  We report repayments to <span className="font-semibold text-[#132A13]">{SITE.CREDIT_BUREAU}</span>,
                  so paying on time can help you build a credit history.
                </Bullet>
              )}
              <Bullet>
                Card and bank payments are processed by Paystack (Nigeria) and Stripe (international). We never see
                your full card number.
              </Bullet>
            </ul>
          </div>
        )}

        {/* Team */}
        {has(SITE.TEAM) && (
          <div className="mt-14" id="team">
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">The team</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-6">
              {SITE.TEAM.map((m) => (
                <div key={m.name} className="rounded-2xl bg-white border border-[#E5E3DB] p-5">
                  {m.photo && (
                    <img src={m.photo} alt={m.name} className="w-16 h-16 rounded-full object-cover" />
                  )}
                  <h3 className="font-display text-lg font-medium text-[#132A13] mt-3">{m.name}</h3>
                  <p className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold mt-0.5">{m.role}</p>
                  {m.bio && <p className="text-sm text-[#4F5D4F] mt-2 leading-relaxed">{m.bio}</p>}
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#0A3622] mt-3 inline-flex items-center gap-1 hover:underline"
                    >
                      <LinkedinLogo size={14} weight="fill" /> LinkedIn
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Where to find us */}
        <div className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">Where to find us</h2>
          <p className="mt-4 text-sm sm:text-base text-[#4F5D4F] leading-relaxed">
            {SITE.COMPANY_LEGAL_NAME}, {SITE.REGISTERED_ADDRESS}. Support is open {SITE.SUPPORT_HOURS} by phone, email
            or WhatsApp. {SITE.LENDING_ENABLED && "Struggling with a payment? Call us before the due date and we'll work it out together."}
          </p>
          <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {has(SITE.SUPPORT_EMAIL) && (
              <li>
                <a href={`mailto:${SITE.SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 text-[#0A3622] hover:underline">
                  <EnvelopeSimple size={16} weight="fill" /> {SITE.SUPPORT_EMAIL}
                </a>
              </li>
            )}
            {has(SITE.PHONE_1) && (
              <li>
                <a href={`tel:${SITE.PHONE_1.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-[#0A3622] hover:underline">
                  <Phone size={16} weight="fill" /> {SITE.PHONE_1}
                </a>
              </li>
            )}
            {has(SITE.WHATSAPP) && (
              <li>
                <a
                  href={`https://wa.me/${SITE.WHATSAPP.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[#0A3622] hover:underline"
                >
                  <WhatsappLogo size={16} weight="fill" /> WhatsApp
                </a>
              </li>
            )}
          </ul>
          {has(SITE.OFFICES) && (
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {SITE.OFFICES.map((o) => (
                <li key={o.city} className="rounded-xl bg-white border border-[#E5E3DB] p-4 flex items-start gap-3">
                  <MapPin size={18} weight="fill" className="text-[#0A3622] mt-0.5" />
                  <div>
                    <div className="font-semibold text-[#132A13]">{o.city}</div>
                    <div className="text-sm text-[#4F5D4F]">{o.address}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <RepExample />
      <Footer />
    </div>
  );
}

function Card({ tag, title, body }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E3DB] p-5 sm:p-6">
      <div className="overline mb-2">{tag}</div>
      <h3 className="font-display text-lg font-medium text-[#132A13]">{title}</h3>
      <p className="text-sm text-[#4F5D4F] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function HowItem({ n, h, b }) {
  return (
    <li className="flex gap-4 items-start">
      <div className="w-8 h-8 rounded-full bg-[#0A3622] text-[#F2B632] grid place-items-center font-mono text-sm font-semibold shrink-0">
        {n}
      </div>
      <div>
        <h3 className="font-display text-lg font-medium text-[#132A13]">{h}</h3>
        <p className="text-sm text-[#4F5D4F] mt-1 leading-relaxed">{b}</p>
      </div>
    </li>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3">
      <CheckCircle size={18} weight="fill" className="text-[#0A3622] mt-0.5 shrink-0" />
      <span className="text-[#132A13] leading-relaxed">{children}</span>
    </li>
  );
}
